import { Button } from '@creditornot/cb-button';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { cbSemanticCssVars, elevationCss } from '@creditornot/cb-ingredients';
import { Input, InputAlert } from '@creditornot/cb-input';
import { ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { WoltModal } from 'common/components';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import EmptyList from 'modules/menu/components/EmptyList';
import useMenuData from 'modules/menu/useMenuData';

import OptionRowItem from './OptionRowItem';

type Props = {
  loading?: boolean;
  mode: 'bulkAction' | 'singleItem';
  onClose: () => void;
  onCreateOption?: () => void;
  onDeselectOption: (id: string) => void;
  isOpen: boolean;
  onSelectOption: (id: string) => void;
  onDone: () => void;
  selectedOptionIds: Set<string>;
  title: React.ReactNode;
};

const Root = styled(WoltModal)`
  min-width: 320px;
  max-width: 640px;
  height: 80vh;
  background-color: ${cbSemanticCssVars.surfaceMain};
  padding: 0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  color: ${cbSemanticCssVars.textPrimary};
`;

const DuplicateInfoContainer = styled.div`
  padding: 16px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const SearchContainer = styled.div<{ elevated: boolean }>`
  padding: 16px 24px;
  transition: box-shadow 200ms;
  z-index: 1;
  display: flex;

  ${({ elevated }) =>
    elevated &&
    css`
      ${elevationCss(2)}
    `}
`;

const SearchInput = styled(Input).attrs({ icon: <MagnifyingGlass /> })``;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const Footer = styled(ModalFooter)`
  justify-content: flex-end;
`;

const CreateOptionButton = styled(Button).attrs({ variant: 'secondary', size: 'small' })`
  margin-inline-start: 12px;
  min-width: 200px;
`;

const CancelButton = styled(Button).attrs({ variant: 'secondary', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'primary', size: 'small' })``;

const OptionBindingsModal = ({
  loading,
  mode,
  onClose,
  onCreateOption,
  onDeselectOption,
  isOpen,
  onSelectOption,
  onDone,
  selectedOptionIds,
  title,
}: Props) => {
  const intl = useIntl();
  const { options } = useMenuData();

  const { primaryLanguage } = useContext(MenuLocaleContext);
  const [searchValue, setSearchValue] = useState('');

  const listRef = useRef<HTMLDivElement>(null);
  const [listScrolled, setListScrolled] = useState(false);

  const handleListScroll = useCallback(() => {
    if (listRef && listRef.current) {
      const { scrollTop } = listRef.current;

      setListScrolled(oldState => {
        if (!oldState && scrollTop > 0) {
          return true;
        }

        if (oldState && scrollTop === 0) {
          return false;
        }

        return oldState;
      });
    }
  }, [listRef]);

  const optionsToRender = useMemo(() => {
    return options
      .map(option => ({
        ...option,
        name: localize(option.name, primaryLanguage),
      }))
      .filter(option => option.name.toLowerCase().includes(searchValue.toLowerCase()));
  }, [options, primaryLanguage, searchValue]);

  return (
    <Root isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>{title}</ModalHeader>
      {mode === 'bulkAction' && (
        <DuplicateInfoContainer>
          <InputAlert variant="info">
            <FormattedMessage id="menu.bulkActions.addOptionsToItems.duplicateInfoMessage" />
          </InputAlert>
        </DuplicateInfoContainer>
      )}
      <SearchContainer elevated={listScrolled}>
        <SearchInput
          onClearClick={() => setSearchValue('')}
          onChange={e => setSearchValue(e.target.value)}
          value={searchValue}
          placeholder={intl.formatMessage({ id: 'menu.bulkActions.addOptionsToItems.searchForOption' })}
        />
        {mode === 'singleItem' && onCreateOption && (
          <CreateOptionButton onClick={onCreateOption}>
            <FormattedMessage id="menu-item.create-new-option" />
          </CreateOptionButton>
        )}
      </SearchContainer>
      <OptionList ref={listRef} onScroll={handleListScroll}>
        {optionsToRender.length > 0 ? (
          optionsToRender.map(option => (
            <OptionRowItem
              key={option.id}
              onDeselect={() => onDeselectOption(option.id)}
              onSelect={() => onSelectOption(option.id)}
              option={option}
              selected={selectedOptionIds.has(option.id)}
            />
          ))
        ) : (
          <EmptyList message={<FormattedMessage id="menu.bulkActions.addOptionsToItems.noOptionsFound" />} />
        )}
      </OptionList>
      <Footer>
        <CancelButton onClick={onClose}>
          <FormattedMessage id="state.cancel" />
        </CancelButton>
        <SubmitButton onClick={onDone} disabled={selectedOptionIds.size === 0} loading={loading}>
          <FormattedMessage id="state.done" />
        </SubmitButton>
      </Footer>
    </Root>
  );
};

export default OptionBindingsModal;
